import { useCallback, useMemo, useRef, useState } from "react";
import type { Comment } from "@/types";

const REACTION_EMOJIS = ["👍", "🔥", "💪", "🎯", "🙌", "👏"];

function formatRelativeTime(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d`;
  return new Date(dateStr).toLocaleDateString();
}

export function CommentsSection({
  comments,
  usernames,
  currentUserId,
  onAddComment,
  onReply,
  onToggleReaction,
  loading,
}: {
  comments: Comment[];
  usernames: string[];
  currentUserId: string;
  onAddComment: (body: string) => Promise<void>;
  onReply: (parentId: string, body: string) => Promise<void>;
  onToggleReaction: (commentId: string, emoji: string) => Promise<void>;
  loading: boolean;
}) {
  const [newComment, setNewComment] = useState("");
  const [replyTo, setReplyTo] = useState<{ id: string; author: string } | null>(null);
  const [replyText, setReplyText] = useState("");
  const [sending, setSending] = useState(false);
  const [mentionFilter, setMentionFilter] = useState("");
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const topLevel = useMemo(
    () =>
      comments
        .filter((c) => !c.parentId)
        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()),
    [comments],
  );

  const getReplies = useCallback(
    (parentId: string) =>
      comments
        .filter((c) => c.parentId === parentId)
        .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()),
    [comments],
  );

  const handleSend = async () => {
    const text = newComment.trim();
    if (!text || sending) return;
    setSending(true);
    await onAddComment(text);
    setNewComment("");
    setSending(false);
  };

  const handleSendReply = async () => {
    const text = replyText.trim();
    if (!text || !replyTo || sending) return;
    setSending(true);
    await onReply(replyTo.id, text);
    setReplyText("");
    setReplyTo(null);
    setSending(false);
  };

  const handleMentionInsert = (username: string) => {
    setNewComment((prev) => {
      const withoutTrigger = mentionFilter
        ? prev.slice(0, prev.lastIndexOf(`@${mentionFilter}`))
        : prev;
      const mention = `@${username} `;
      return withoutTrigger + mention;
    });
    setMentionFilter("");
    inputRef.current?.focus();
  };

  const handleCommentChange = (text: string) => {
    setNewComment(text);
    const lastAt = text.lastIndexOf("@");
    if (lastAt >= 0 && (lastAt === 0 || text[lastAt - 1] === " ")) {
      setMentionFilter(text.slice(lastAt + 1));
    } else if (mentionFilter) {
      setMentionFilter("");
    }
  };

  const filteredUsernames = mentionFilter
    ? usernames.filter((u) => u.toLowerCase().startsWith(mentionFilter.toLowerCase()))
    : [];

  return (
    <div className="flex flex-col gap-4" aria-live="polite">
      <h3 className="text-text text-lg font-extrabold tracking-tight">Comments</h3>

      <div className="relative">
        <textarea
          ref={inputRef as React.RefObject<HTMLTextAreaElement>}
          value={newComment}
          onChange={(e) => handleCommentChange(e.target.value)}
          placeholder="Add a comment... (type @ to mention)"
          className="w-full bg-card rounded-2xl border border-text px-3.5 py-3 text-text text-sm font-inherit min-h-[5rem] resize-y"
        />
        {filteredUsernames.length > 0 && (
          <div className="absolute bottom-full left-0 mb-1 bg-card rounded-xl border border-[rgba(28,27,46,0.2)] shadow-[1px_1px_0px_0px_#1c1b2e] overflow-hidden z-10">
            {filteredUsernames.slice(0, 5).map((username) => (
              <button
                type="button"
                key={username}
                onClick={() => handleMentionInsert(username)}
                className="block w-full text-left px-3 py-2 text-sm text-text font-bold cursor-pointer bg-[rgba(0,0,0,0.02)] hover:bg-[rgba(255,45,126,0.06)] font-inherit border-0"
              >
                @{username}
              </button>
            ))}
          </div>
        )}
        <div className="flex justify-end mt-2">
          <button
            type="button"
            onClick={handleSend}
            disabled={!newComment.trim() || sending}
            className="bg-aqua text-white rounded-2xl px-4 py-2 text-sm font-extrabold cursor-pointer border border-text shadow-[1px_1px_0px_0px_#1c1b2e] font-inherit disabled:opacity-40 disabled:cursor-default"
          >
            {sending ? "Sending..." : "Post"}
          </button>
        </div>
      </div>

      {loading && (
        <div className="animate-pulse flex flex-col gap-3">
          {Array.from({ length: 3 }, (_, i) => i).map((n) => (
            <div key={n} className="bg-[rgba(28,27,46,0.08)] rounded-xl h-16" />
          ))}
        </div>
      )}

      {!loading && topLevel.length === 0 && (
        <p className="text-text-muted text-sm italic text-center py-4">
          No comments yet. Be the first!
        </p>
      )}

      <div className="flex flex-col gap-3">
        {topLevel.map((comment) => (
          <CommentCard
            key={comment.id}
            comment={comment}
            replies={getReplies(comment.id)}
            currentUserId={currentUserId}
            onToggleReaction={onToggleReaction}
            onReply={() => setReplyTo({ id: comment.id, author: comment.authorDisplayName })}
            replyTo={replyTo}
            replyText={replyText}
            onReplyTextChange={setReplyText}
            onSendReply={handleSendReply}
            onCancelReply={() => {
              setReplyTo(null);
              setReplyText("");
            }}
            sending={sending}
          />
        ))}
      </div>
    </div>
  );
}

function CommentCard({
  comment,
  replies,
  currentUserId,
  onToggleReaction,
  onReply,
  replyTo,
  replyText,
  onReplyTextChange,
  onSendReply,
  onCancelReply,
  sending,
}: {
  comment: Comment;
  replies: Comment[];
  currentUserId: string;
  onToggleReaction: (commentId: string, emoji: string) => Promise<void>;
  onReply: () => void;
  replyTo: { id: string; author: string } | null;
  replyText: string;
  onReplyTextChange: (text: string) => void;
  onSendReply: () => void;
  onCancelReply: () => void;
  sending: boolean;
}) {
  const isReply = !!comment.parentId;

  return (
    <div
      className={`flex flex-col ${isReply ? "ml-8 border-l-2 border-[rgba(28,27,46,0.1)] pl-3" : ""}`}
    >
      <div className="bg-card rounded-xl border border-[rgba(28,27,46,0.15)] p-3 shadow-[1px_1px_0px_0px_#1c1b2e]">
        <div className="flex items-center gap-2 mb-1.5">
          <div className="w-6 h-6 rounded-full bg-aqua flex items-center justify-center text-white text-[0.625rem] font-extrabold shrink-0">
            {comment.authorDisplayName.charAt(0).toUpperCase()}
          </div>
          <span className="text-text text-xs font-bold">{comment.authorDisplayName}</span>
          {comment.authorUserId === currentUserId && (
            <span className="text-text-dim text-[0.625rem] font-bold">You</span>
          )}
          <span className="text-text-dim text-[0.625rem] ml-auto">
            {formatRelativeTime(comment.createdAt)}
          </span>
        </div>

        <p className="text-text text-sm leading-5 whitespace-pre-wrap">{comment.body}</p>

        <div className="flex items-center gap-2 mt-2">
          {REACTION_EMOJIS.map((emoji) => {
            const reaction = comment.reactions?.find((r) => r.emoji === emoji);
            const count = reaction?.count ?? 0;
            const hasReacted = reaction?.hasReacted ?? false;
            return (
              <button
                type="button"
                key={emoji}
                onClick={() => onToggleReaction(comment.id, emoji)}
                className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-xs border cursor-pointer font-inherit ${
                  hasReacted
                    ? "bg-[rgba(255,45,126,0.1)] border-aqua text-aqua"
                    : "bg-white border-[rgba(28,27,46,0.15)] text-text-muted"
                }`}
              >
                <span>{emoji}</span>
                {count > 0 && <span className="font-bold">{count}</span>}
              </button>
            );
          })}

          {!isReply && (
            <button
              type="button"
              onClick={onReply}
              className="text-text-dim text-xs font-bold cursor-pointer bg-transparent border-0 font-inherit ml-auto"
            >
              Reply
            </button>
          )}
        </div>
      </div>

      {replies.map((reply) => (
        <CommentCard
          key={reply.id}
          comment={reply}
          replies={[]}
          currentUserId={currentUserId}
          onToggleReaction={onToggleReaction}
          onReply={() => {}}
          replyTo={null}
          replyText=""
          onReplyTextChange={() => {}}
          onSendReply={async () => {}}
          onCancelReply={() => {}}
          sending={false}
        />
      ))}

      {replyTo?.id === comment.id && (
        <div className="ml-8 mt-2">
          <div className="bg-card rounded-xl border border-[rgba(28,27,46,0.15)] p-3 flex flex-col gap-2">
            <span className="text-text-dim text-xs font-bold">
              Replying to <span className="text-aqua">{replyTo.author}</span>
            </span>
            <textarea
              value={replyText}
              onChange={(e) => onReplyTextChange(e.target.value)}
              placeholder="Write a reply..."
              className="w-full bg-white rounded-xl border border-text px-3 py-2 text-text text-sm font-inherit min-h-[3.75rem] resize-y"
            />
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={onCancelReply}
                className="text-text-muted text-xs font-bold cursor-pointer bg-transparent border-0 font-inherit"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={onSendReply}
                disabled={!replyText.trim() || sending}
                className="bg-aqua text-white rounded-xl px-3 py-1.5 text-xs font-extrabold cursor-pointer border border-text shadow-[1px_1px_0px_0px_#1c1b2e] font-inherit disabled:opacity-40 disabled:cursor-default"
              >
                {sending ? "..." : "Reply"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
